/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormattedDate } from 'react-intl';
import { LocalDate, nativeJs } from 'js-joda';
import { useCallback, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import _ from 'lodash';
import clsx from 'clsx';

import * as time from '@tock/shared/utils/time';
import { CalendarNavBarV5 } from './CalendarNavBarV5';
import { DayPickerProps, IWithContextProps, withContext } from '@tock/coretypes';
import { DisplayWeekday } from '@tock/time';
import { addOrReplaceDate } from 'utils/multiDateUtils';
import DefaultCalendarDayV2 from 'components/calendar/DefaultCalendarDayV2';
import YearMonthFormV2 from 'components/calendar/YearMonthPickerV2';

export function defaultRenderDay(day: Date, modifiers: any) {
  return <DefaultCalendarDayV2 day={day} modifiers={modifiers} />;
}

export interface Props {
  allowMultipleDates?: boolean;
  disablePastDates?: boolean;
  disableAfterDate?: LocalDate;
  initialDate?: LocalDate | LocalDate[];
  className?: string;
  renderDay?(day: Date, modifiers: any): JSX.Element;
  selectedDate?: LocalDate | LocalDate[];
  onSelectDate(date: LocalDate): void;
}

export const DAY_PICKER_OUTERMOST_WRAPPER_ID = 'day-picker-outermost-wrapper';

function CalendarV5(props: Props & IWithContextProps): JSX.Element {
  const { allowMultipleDates, initialDate, selectedDate: propSelectedDate } = props;

  // Keep a stable "today" like the class did (set once).
  const todayRef = useRef<LocalDate>(LocalDate.now());
  const today = todayRef.current;

  // initialize initialMonth once (mimic constructor behavior)
  const computeInitialMonth = () => {
    const initialMonth =
      _.isArray(initialDate) && initialDate
        ? (initialDate as LocalDate[])[0]
        : (initialDate as LocalDate);
    return initialMonth || today;
  };
  const [initialMonth] = useState<LocalDate>(computeInitialMonth);

  // initialize selectedDate once (mimic constructor behavior)
  const computeInitialSelected = () => {
    if (propSelectedDate) {
      return propSelectedDate;
    }
    return allowMultipleDates ? ([today] as LocalDate[]) : (today as LocalDate);
  };
  const [selectedDate, setSelectedDateState] = useState<LocalDate | LocalDate[]>(
    computeInitialSelected
  );

  // dayPicker node ref
  const dayPickerNodeRef = useRef<any | null>(null);
  const handleSetRef = useCallback((nodeRef: any) => {
    dayPickerNodeRef.current = nodeRef;
  }, []);

  // handler to set selectedDate state and show month on DayPicker (mimics instance method)
  // this wasn't used in the class component
  // const setSelectedDate = useCallback((next: LocalDate) => {
  //   setSelectedDateState(next);
  //   if (dayPickerNodeRef.current && typeof dayPickerNodeRef.current.showMonth === 'function') {
  //     dayPickerNodeRef.current.showMonth(time.toLocalJSDate(next));
  //   }
  // }, []);

  // isSelected function used by DayPicker.selectedDays
  const isSelected = useCallback(
    (dateObj: any) => {
      const normalizedDate = LocalDate.from(nativeJs(dateObj));
      return _.isArray(selectedDate)
        ? !!_.find(selectedDate as LocalDate[], (date) => date.equals(normalizedDate))
        : normalizedDate.isEqual(selectedDate as LocalDate);
    },
    [selectedDate]
  );

  // day click handler
  const handleDayClick = useCallback(
    (dateObj: Date, modifiers: any) => {
      if (modifiers && modifiers.disabled) {
        return;
      }

      const normalizedDate = LocalDate.from(nativeJs(dateObj));
      props.onSelectDate(normalizedDate);

      if (!allowMultipleDates || !_.isArray(selectedDate)) {
        setSelectedDateState(normalizedDate);
      } else {
        const currentDates = addOrReplaceDate(selectedDate as LocalDate[], normalizedDate);
        setSelectedDateState(currentDates);
      }
    },
    [allowMultipleDates, props, selectedDate]
  );

  // disable past / after date logic
  const disablePastDatesFn = useCallback(
    (dateObj: Date) => {
      const { disablePastDates, disableAfterDate } = props;
      const date = LocalDate.from(nativeJs(dateObj));
      const isBeforeToday = disablePastDates && date.isBefore(today);
      const isAfterDate = disableAfterDate && date.isAfter(disableAfterDate);
      return isBeforeToday || isAfterDate;
    },
    [props, today]
  );

  // year/month change handler -> showMonth on DayPicker
  const handleYearMonthChange = useCallback((monthDate: Date) => {
    if (dayPickerNodeRef.current && typeof dayPickerNodeRef.current.showMonth === 'function') {
      dayPickerNodeRef.current.showMonth(monthDate);
    }
  }, []);

  const {
    className,
    disableAfterDate,
    disablePastDates,
    context: { locale },
    renderDay,
  } = props;

  const dayPickerProps: DayPickerProps = {
    fixedWeeks: true,
    initialMonth: time.toLocalJSDate(initialMonth),
    localeUtils: {
      formatDay: (dateObj: Date) => {
        return LocalDate.from(nativeJs(dateObj)).toString();
      },
      formatWeekdayShort(i: any) {
        return <DisplayWeekday displayWeekdayAs="short" weekdayValue={i} />;
      },
      formatWeekdayLong(i: any) {
        return <DisplayWeekday displayWeekdayAs="long" weekdayValue={i} />;
      },
      formatMonthTitle: (date: any) => {
        return <FormattedDate locales={[locale]} month="long" value={date} year="numeric" />;
      },
      getFirstDayOfWeek() {
        return 0;
      },
    },
    locale,
    renderDay: renderDay || defaultRenderDay,
    selectedDays: isSelected,
    onDayClick: handleDayClick,
  };

  if (disablePastDates || disableAfterDate) {
    // Note: Setting fromMonth on the dayPicker disables navigation to past months
    dayPickerProps.disabledDays = disablePastDatesFn;
    dayPickerProps.fromMonth = time.toJSDate(today);
  }

  return (
    <div className={clsx('Calendar', className)} data-testid={DAY_PICKER_OUTERMOST_WRAPPER_ID}>
      <div className="Calendar-content">
        <DayPicker
          {...dayPickerProps}
          ref={handleSetRef}
          captionElement={({ date }: { date: Date }) => (
            <YearMonthFormV2 date={date} onChange={handleYearMonthChange} />
          )}
          navbarElement={CalendarNavBarV5}
        />
      </div>
    </div>
  );
}

export default withContext(CalendarV5);
